// @file <pdlt-mention> custom HTML5 element. Used to render a mention on Padlet.

import { currentHostname } from '@@/bits/location'

const SUPPORTED_MENTION_TYPES = ['user'] as const
type MentionType = typeof SUPPORTED_MENTION_TYPES[number]

/**
 * A custom HTML5 element used to render a mention on Padlet.
 * If the type is "user", the element renders an anchor tag that links to the mentioned user's profile.
 *
 * @example
 * <pdlt-mention
 *   type="user"
 *   mentioner-id="yLA6m0oM"
 *   mentionee-id="bUYIm767"
 *   mentionee-username="pizza"
 * >
 *   Pizza Boy
 * </pdlt-mention>
 *
 * @param {string} type - The type of mention. Currently only `user` is supported.
 * @param {string} mentioner-id - The public ID of the user who is mentioning someone.
 * @param {string} mentionee-id - The public ID of the user who is being mentioned.
 * @param {string} mentionee-username - The username of the user who is being mentioned.
 */
class PdltMention extends HTMLElement {
  get type(): MentionType | null {
    const rawType = this.getAttribute('type')
    if (rawType === null) return null
    const castedType = rawType as MentionType
    if (SUPPORTED_MENTION_TYPES.includes(castedType)) return castedType
    return null
  }

  get mentionerId(): string | null {
    return this.getAttribute('mentioner-id')
  }

  get mentioneeId(): string | null {
    return this.getAttribute('mentionee-id')
  }

  get mentioneeUsername(): string | null {
    return this.getAttribute('mentionee-username')
  }

  connectedCallback(): void {
    if (this.type === 'user') {
      this.renderProfileLink()
    }
  }

  private renderProfileLink(): void {
    if (this.mentioneeUsername != null && this.mentioneeUsername !== '') {
      const profileLink = `https://${currentHostname()}/${this.mentioneeUsername}`
      this.innerHTML = `<a href="${profileLink}" target="_blank">${this.innerText}</a>`
    }
  }
}

export { PdltMention }
